import {
  HORIZON,
  type Edit,
  type EggPlace,
  type FlyPlacement,
  type MomentEvent,
  type WorldState,
} from "./types";

// --- Начальная раскладка (момент 0) -----------------------------------------
// Тропинка улитки: 11 камешков 0..10. Салат по умолчанию на камешке 6.
// Лягушка в пруду, муха кружит над камышом, курица в гнезде, яйцо на мху.
export const SALAD_DEFAULT_PEBBLE = 6;
export const FLY_REEDS = { x: 520, y: 330 };
export const LILY_LEAF = { x: 300, y: 590 };

function initialState(): WorldState {
  return {
    moment: 0,
    snail: { pebble: 0, eating: false, hungrySleep: false },
    salad: { pebble: SALAD_DEFAULT_PEBBLE },
    frog: { place: "pond", hasFly: false },
    fly: { place: "reeds", x: FLY_REEDS.x, y: FLY_REEDS.y, eaten: false },
    hen: { place: "nest", wantsChick: false },
    egg: { place: "moss", warmth: 0, hatched: false, hatchedAt: null },
    chick: { exists: false, crying: false, underWing: false, asleep: false },
    events: [],
  };
}

// --- Разрешение положения предмета из правок -------------------------------
// Положение предмета в момент k = значение из последней правки с tick ≤ k,
// иначе исходное. (GDD 4.5)
function eggPlaceAt(edits: Edit[], k: number): EggPlace {
  let place: EggPlace = "moss";
  for (const e of edits) {
    if (e.objectId === "egg" && e.tick <= k && e.placement.kind === "egg") place = e.placement.place;
  }
  return place;
}

function saladPebbleAt(edits: Edit[], k: number): number {
  let pebble = SALAD_DEFAULT_PEBBLE;
  for (const e of edits) {
    if (e.objectId === "salad" && e.tick <= k && e.placement.kind === "salad") pebble = e.placement.pebble;
  }
  return pebble;
}

function flyPlacementAt(edits: Edit[], k: number): { placed: boolean; tick: number; value: FlyPlacement } {
  let out = { placed: false, tick: -1, value: { onLeaf: false, x: FLY_REEDS.x, y: FLY_REEDS.y } };
  for (const e of edits) {
    if (e.objectId === "fly" && e.tick <= k && e.placement.kind === "fly") {
      out = { placed: true, tick: e.tick, value: e.placement.value };
    }
  }
  return out;
}

// --- Расписания жителей ----------------------------------------------------
// Лягушка на листе на 3, 6, 9; ныряет на 4, 7, 10 (GDD 4.3 / 4.6).
const FROG_JUMP_MOMENTS = [3, 6, 9];
function frogOnLeaf(m: number): boolean {
  return FROG_JUMP_MOMENTS.includes(m);
}
// Курица в гнезде 0..2 и 8..10, на заборе 3..7 (GDD 4.4).
function henInNest(m: number): boolean {
  return m <= 2 || m >= 8;
}

// --- Шаг симуляции: state[m] из state[m-1] --------------------------------
function step(prev: WorldState, m: number, edits: Edit[]): WorldState {
  const events: MomentEvent[] = [{ kind: "tick", moment: m }];
  const s: WorldState = structuredClone(prev);
  s.moment = m;
  s.events = events;

  // --- Улитка + салат ---
  const L = saladPebbleAt(edits, m);
  s.salad.pebble = L;
  if (!prev.snail.eating && !prev.snail.hungrySleep) {
    const next = Math.min(prev.snail.pebble + 1, HORIZON);
    if (next >= L && L >= prev.snail.pebble) {
      s.snail.pebble = L;
      s.snail.eating = true;
      events.push({ kind: "snailEat", moment: m });
    } else {
      s.snail.pebble = next;
      events.push({ kind: "snailStep", moment: m });
      if (next >= HORIZON) {
        s.snail.hungrySleep = true;
        events.push({ kind: "snailHungry", moment: m });
      }
    }
  } else if (prev.snail.eating) {
    events.push({ kind: "snailEat", moment: m });
  }

  // --- Лягушка + муха ---
  const wasLeaf = prev.frog.place === "leaf";
  const nowLeaf = frogOnLeaf(m);
  s.frog.place = nowLeaf ? "leaf" : "pond";
  if (nowLeaf && !wasLeaf) events.push({ kind: "frogJump", moment: m });
  if (!nowLeaf && wasLeaf) events.push({ kind: "frogDive", moment: m });

  const fp = flyPlacementAt(edits, m);
  if (fp.placed) {
    s.fly.place = fp.value.onLeaf ? "leaf" : "free";
    s.fly.x = fp.value.x;
    s.fly.y = fp.value.y;
  } else {
    s.fly.place = "reeds";
    s.fly.x = FLY_REEDS.x;
    s.fly.y = FLY_REEDS.y;
  }
  // Поимка: лягушка на 3/6/9 ловит муху, если она на листе, ещё не съедена
  // и посажена не позже этого прыжка. Даёт исходы p≤3→3, 4..6→6, 7..9→9. (GDD 4.6)
  if (nowLeaf && !s.frog.hasFly && !prev.fly.eaten && fp.placed && fp.value.onLeaf && fp.tick <= m) {
    s.frog.hasFly = true;
    s.fly.eaten = true;
    events.push({ kind: "frogCatch", moment: m });
  } else {
    s.fly.eaten = prev.fly.eaten;
    s.frog.hasFly = prev.frog.hasFly;
  }

  // --- Курица ---
  const wasNest = prev.hen.place === "nest";
  const nowNest = henInNest(m);
  s.hen.place = nowNest ? "nest" : "fence";
  if (!nowNest && wasNest) events.push({ kind: "henFly", moment: m });
  if (nowNest && !wasNest) events.push({ kind: "henReturn", moment: m });

  // --- Яйцо: копит тепло в гнезде, +1 за момент, вылупление на 5-й точке ---
  const eggHere = eggPlaceAt(edits, m);
  const eggPrev = eggPlaceAt(edits, m - 1);
  s.egg.place = eggHere;
  if (!prev.egg.hatched && eggHere === "nest" && eggPrev === "nest") {
    s.egg.warmth = Math.min(prev.egg.warmth + 1, 5);
    events.push({ kind: "eggWarm", moment: m });
    if (s.egg.warmth >= 5) {
      s.egg.hatched = true;
      s.egg.hatchedAt = m;
      events.push({ kind: "eggHatch", moment: m });
    }
  } else if (eggHere === "moss" && !prev.egg.hatched) {
    // Яйцо вынули из гнезда до вылупления — тепло сбрасывается.
    s.egg.warmth = 0;
  }

  // --- Цыплёнок ---
  s.chick.exists = s.egg.hatched;
  s.chick.underWing = s.chick.exists && s.hen.place === "nest";
  s.chick.crying = s.chick.exists && !s.chick.underWing && m < HORIZON;
  s.chick.asleep = s.chick.exists && m >= HORIZON;
  if (s.chick.crying && !prev.chick.crying) events.push({ kind: "chickCry", moment: m });
  if (s.chick.underWing && !prev.chick.underWing) events.push({ kind: "chickUnderWing", moment: m });

  // Курица хочет цыплёнка (облачко), если гнездо пустое и яйцо не в гнезде.
  s.hen.wantsChick = !s.egg.hatched && eggHere !== "nest" && (m <= 2 || m === 8);

  // --- Цветок ---
  if (m === 5) events.push({ kind: "flowerOpen", moment: m });
  if (m === 9) events.push({ kind: "flowerClose", moment: m });

  // --- Кукушка на 10 ---
  if (m === HORIZON) events.push({ kind: "cuckoo", moment: m });

  return s;
}

// --- simulate + кэш ------------------------------------------------------
// Одиннадцать состояний (0..10), пересчёт мгновенный. Кэш инвалидируется
// при любом изменении набора правок (по «подписи» edits).
let cache: WorldState[] | null = null;
let cacheSig = "";

function signature(edits: Edit[]): string {
  return edits.map((e) => `${e.objectId}@${e.tick}:${JSON.stringify(e.placement)}`).join("|");
}

export function simulateAll(edits: Edit[]): WorldState[] {
  const sig = signature(edits);
  if (cache && sig === cacheSig) return cache;
  const states: WorldState[] = [initialState()];
  for (let m = 1; m <= HORIZON; m++) states.push(step(states[m - 1], m, edits));
  cache = states;
  cacheSig = sig;
  return states;
}

export function stateAt(edits: Edit[], k: number): WorldState {
  const clamped = Math.max(0, Math.min(HORIZON, Math.round(k)));
  return simulateAll(edits)[clamped];
}

export { FROG_JUMP_MOMENTS };
